import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import ViewImage from '../Img/components/ViewImage';
import PageLoader from './PageLoader';

export const rowLabelPhoto = ({
  labelKey,
  photoKey,
  label,
  srcExtension = '',
  ...rest
}) => ({
  key: (item) => (
    <div className="flex items-center space-x-3">
      <div>
        <ViewImage
          alt={item[labelKey]}
          withLightBox={false}
          heightClassName="h-10"
          src={`${item[photoKey]}${srcExtension}`}
          hoverLabel={<span>View</span>}
          className="object-cover object-center w-10 rounded"
        />
      </div>
      {typeof labelKey === 'function' ? (
        labelKey(item)
      ) : (
        <span className="font-medium">{item[labelKey]}</span>
      )}
    </div>
  ),
  label,
  ...rest,
});

const renderHeader = (col) => {
  try {
    return typeof col.label === 'function' ? col?.label(col) : col?.label;
  } catch (error) {
    return '';
  }
};

const getCell = (item, col, i) => {
  try {
    return typeof col.key === 'function' ? col.key(item, i) : item?.[col?.key];
  } catch (err) {
    return '';
  }
};

function RenderLoader({ renderLabel }) {
  const [render, setRender] = React.useState(0);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setRender(1);
    }, 500);
    return () => clearInterval(timer);
  }, []);

  if (render) {
    return (
      <div className="h-full w-full bg-white flex rounded overflow-hidden">
        {renderLabel}
      </div>
    );
  }
  return null;
}

RenderLoader.propTypes = {
  renderLabel: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
    .isRequired,
};

function GenericTable({
  id,
  data,
  format,
  selected,
  isLoading,
  withHover,
  showHeader,
  pagination,
  tClassName,
  selectedKey,
  onSelectRow,
  noRowsLabel,
  trClassName,
  tbodyClassName,
  theadClassName,
  containerClassName,
}) {
  const [rows, setRows] = React.useState([]);

  const handleSelectRow = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    // This is brakepoint of tailwind sm screen size
    const width = window.outerWidth;
    if (onSelectRow && width > 640) {
      onSelectRow(value);
    }
  };

  React.useEffect(() => {
    if (!isLoading) {
      setRows(data);
    }
  }, [data, isLoading]);

  React.useLayoutEffect(() => {
    let timer;
    if (selected) {
      const x = document.getElementById(selected);
      timer = setTimeout(() => {
        if (rows?.length && withHover && selected && x) {
          x.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
    return () => clearInterval(timer);
  }, [rows, selected, withHover]);

  const $format = React.useMemo(() => format?.filter((x) => x), [format]);

  return (
    <div className="relative w-full h-full md:min-h-0 overflow-hidden bg-white rounded">
      {rows.length > 0 && isLoading && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 h-[98%] w-[99%] bg-white rounded flex flex-col">
          <div className="md:m-auto">
            <PageLoader />
          </div>
        </div>
      )}
      {rows.length === 0 && isLoading && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 h-[98%] w-[99%] bg-white rounded flex flex-col">
          <div className="md:m-auto">
            <PageLoader />
          </div>
        </div>
      )}
      {rows?.length === 0 && !isLoading && (
        <div className="flex justify-center">
          <div className="absolute z-10 inset-1">
            <RenderLoader renderLabel={noRowsLabel} />
          </div>
        </div>
      )}
      <div
        id="_table-container"
        className={cn('_table-container', {
          [containerClassName]: containerClassName,
        })}
      >
        <div id={id} className="_table-inner-container">
          <table
            className={cn('_table', {
              [tClassName]: tClassName,
            })}
          >
            {showHeader && (
              <thead
                className={cn('_table-head', {
                  [theadClassName]: theadClassName,
                })}
              >
                <tr>
                  {$format.map((col) => {
                    const {
                      stackKey,
                      widthClassName = false,
                      stackKeyCLassName = 'hidden md:table-cell',
                      column_id = null,
                    } = col;
                    return (
                      <th
                        key={column_id || col.label}
                        className={cn('', {
                          [stackKeyCLassName]: stackKey,
                          [widthClassName]: widthClassName,
                        })}
                      >
                        {renderHeader(col)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody
              className={cn('_table-body', {
                [tbodyClassName]: tbodyClassName,
              })}
            >
              {rows.length < 1 ? (
                <tr />
              ) : (
                rows.map((item, i) => {
                  const isSelected = `${selected}` === `${item[selectedKey]}`;
                  const key = item[selectedKey];

                  const current_page = pagination?.current_page ?? 0;
                  const per_page = pagination?.per_page ?? 0;

                  return (
                    <tr
                      key={key}
                      id={item?.[selectedKey]}
                      className={cn('_table-row', {
                        '_table-row-selected': isSelected,
                        [trClassName]: trClassName,
                        '_table-row-hover': withHover && !isSelected,
                        '!cursor-default': !onSelectRow,
                      })}
                      onClick={onSelectRow ? handleSelectRow(item) : () => {}}
                    >
                      {$format.map((col) => {
                        const {
                          stackKey,
                          widthClassName = false,
                          stackKeyCLassName = 'hidden md:table-cell',
                          column_id = null,
                        } = col;

                        if (col.key === '_count') {
                          return (
                            <td
                              key={`${key}-${col.label}`}
                              className={cn({
                                [widthClassName]: widthClassName,
                              })}
                            >
                              <span className="font-semibold">
                                {(current_page - 1) * per_page + i + 1}.
                              </span>
                            </td>
                          );
                        }
                        return (
                          <td
                            key={column_id || `${key}-${col.label}`}
                            className={cn({
                              [stackKeyCLassName]: stackKey,
                              [widthClassName]: widthClassName,
                            })}
                          >
                            {getCell(item, col, i)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

GenericTable.defaultProps = {
  id: 'generic-table',
  data: [],
  format: [],
  selected: '',
  withHover: false,
  isLoading: false,
  showHeader: true,
  tClassName: false,
  selectedKey: 'id',
  pagination: false,
  trClassName: false,
  onSelectRow: false,
  theadClassName: false,
  tbodyClassName: false,
  containerClassName: false,
  noRowsLabel: 'No data found.',
};

GenericTable.propTypes = {
  id: PropTypes.string,
  withHover: PropTypes.bool,
  isLoading: PropTypes.bool,
  showHeader: PropTypes.bool,
  selectedKey: PropTypes.string,
  pagination: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  data: PropTypes.instanceOf(Array),
  format: PropTypes.instanceOf(Array),
  onSelectRow: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  trClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  tbodyClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  noRowsLabel: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  theadClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default React.memo(GenericTable);
